import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, HelpCircle, Shield, Tv, Crown, Users, Zap, ListMusic, Star, Mail } from 'lucide-react';

const CONTACT_EMAIL = 'support@retroplaygroundtv.com';

const faqs = [
  {
    category: 'Getting Started',
    icon: Tv,
    color: '#FF3860',
    items: [
      { q: 'What is Retro Playground TV?', a: 'Retro Playground TV is a handpicked collection of classic 90s and early 2000s kids shows and cartoons, all embedded from YouTube. We organize calm, gentle-paced selections so families can enjoy the wholesome entertainment you grew up with. We do not host or own any video content — all videos play directly from YouTube.' },
      { q: 'Is Retro Playground TV really free?', a: 'Yes, 100% free. There is no subscription, no paywall, no hidden fees. Every show, playlist, and feature is available to everyone at no cost. We accept optional tips to help cover site costs, but nothing is ever locked behind payment.' },
      { q: 'How do I browse shows?', a: 'Click "Browse" in the top menu. From there you can filter by category (Cartoons or Live Action), by era (Early 90s, Late 90s, Early 2000s), or use the Time Machine to jump to a specific decade. There\'s also a search bar to find shows by name.' },
      { q: 'What is TV Mode?', a: 'TV Mode recreates the retro TV experience! It picks random episodes and sprinkles in vintage commercial breaks from the era, just like real TV. Hit "Channel Surf" to skip to the next show. Great for lazy afternoons and cozy weekends.' },
      { q: 'What is the Time Machine?', a: 'On the Browse page, the Time Machine lets you jump to a specific era. Click "80s Classics," "Early 90s," "Late 90s," or "Early 2000s" to see only shows from that time period. Click again or "Show All" to reset.' },
    ]
  },
  {
    category: 'YouTube & Ad-Free Viewing',
    icon: Star,
    color: '#FFDE00',
    items: [
      { q: 'Can I view shows without YouTube ads?', a: 'Yes! If you have YouTube Premium and are signed into your Google account in the same browser, the embedded videos will play ad-free automatically. No special setup with our site is needed.' },
      { q: 'How do I set up ad-free viewing?', a: 'Step 1: Go to youtube.com/premium and subscribe to YouTube Premium. Step 2: Make sure you\'re signed into that Google account in the same browser you use for Retro Playground TV. Step 3: That\'s it! Videos will play without ads. Your YouTube login stays completely separate from Retro Playground TV.' },
      { q: 'Does Retro Playground TV have access to my YouTube or Google account?', a: 'No. We have zero access to your Google or YouTube data. The YouTube player embedded on our site is operated entirely by YouTube. Your credentials are never shared with us.' },
      { q: 'Why does a video say "unavailable"?', a: 'YouTube videos can be removed or blocked by their uploaders at any time. Since we link to YouTube, this is outside our control. If you see this, please let us know so we can update the link.' },
    ]
  },
  {
    category: 'Parental Controls & Safety',
    icon: Shield,
    color: '#4AC6FF',
    items: [
      { q: 'Is this site safe for kids?', a: 'Yes. Every show and episode is hand-selected. There are no user-uploaded videos, no comments from strangers, no chat, and no external links. Kids see only what has been chosen for them.' },
      { q: 'How do I set a parental PIN?', a: 'Go to Settings (the gear icon in the top menu) and click "Set PIN." Choose a 4-digit number. Once set, the PIN helps keep parent-only areas like admin settings protected. Kids can browse and enjoy shows freely.' },
      { q: 'What are Kid Profiles?', a: 'You can create up to 4 kid profiles, each with a custom name and color. Each child gets their own favorites list. Go to "Kids" in the menu to create and manage profiles.' },
      { q: 'What is Bedtime Mode?', a: 'Bedtime Mode lets you set a daily episode limit (for example, 3 episodes per day). When the limit is reached, a friendly message tells your child it\'s time to rest. You can turn it on and adjust the limit in Settings.' },
    ]
  },
  {
    category: 'Supporting Retro Playground TV',
    icon: Crown,
    color: '#FF6B00',
    items: [
      { q: 'How do donations work?', a: 'Donations are one-time, voluntary tips — $2, $3, $5, or a custom amount. They are processed securely through PayPal. Donors can use a PayPal account or pay with a credit/debit card — no account required. There are no subscriptions, no recurring charges, and no auto-renewals.' },
      { q: 'What do donations go toward?', a: 'Tips go directly toward hosting costs and site maintenance. That\'s it. Donations do not unlock content — everything on Retro Playground TV is free for everyone regardless.' },
      { q: 'Do I have to donate?', a: 'Not at all. Retro Playground TV is completely free. Tipping is entirely optional and simply helps keep the site running.' },
    ]
  },
  {
    category: 'Features',
    icon: ListMusic,
    color: '#39FF14',
    items: [
      { q: 'What are Themed Playlists?', a: 'Shows are organized into themed collections like "Weekend Cartoon Lineup," "Cozy Classics," "Educational Favorites," and more. Each playlist groups shows by mood or theme. Browse them from the Playlists page.' },
      { q: 'Can I leave a review on a show?', a: 'Yes! On any show\'s page, you can rate it 1-5 stars and leave a comment. Reviews help other families find great shows. You need to be signed in to leave a review, and you can update yours anytime.' },
      { q: 'What is the Weekly Featured Show?', a: 'Each week, a different show is highlighted on the homepage. It rotates automatically to help families discover shows they might have missed.' },
      { q: 'Can I save my favorite shows?', a: 'Yes. Click the heart icon on any show page to add it to your Favorites. You can view all your saved shows from the "Favorites" link in the menu. You\'ll need to be signed in.' },
    ]
  },
];

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b-2 border-[#0A0A0A]/10 last:border-b-0" data-testid={`faq-item`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-4 text-left group">
        <span className="font-['Nunito'] font-bold text-[#0A0A0A] text-sm sm:text-base pr-4 group-hover:text-[#FF3860] transition-colors">{item.q}</span>
        {open ? <ChevronUp size={18} strokeWidth={2.5} className="text-[#FF3860] shrink-0" /> : <ChevronDown size={18} strokeWidth={2.5} className="text-[#4B5563] shrink-0" />}
      </button>
      {open && (
        <div className="pb-4 animate-fade-in-up" style={{ animationDuration: '0.2s' }}>
          <p className="text-sm text-[#4B5563] font-['Nunito'] leading-relaxed">{item.a}</p>
        </div>
      )}
    </div>
  );
}

export default function FAQPage() {
  return (
    <div className="page-content bg-[#FDFBEC] min-h-screen">
      <div className="pattern-bg" />
      <div className="max-w-3xl mx-auto px-6 md:px-8 py-12 relative z-10">
        <div className="text-center mb-10">
          <HelpCircle size={36} strokeWidth={2} className="text-[#FF3860] mx-auto mb-3" />
          <h1 className="font-['Fredoka'] text-4xl sm:text-5xl font-black text-[#0A0A0A] tracking-tight mb-3" data-testid="faq-title">
            Help & FAQ
          </h1>
          <p className="text-base text-[#4B5563] font-['Nunito']">Everything you need to know about Retro Playground TV</p>
        </div>

        {/* Contact Us */}
        <a
          href={`mailto:${CONTACT_EMAIL}?subject=Retro%20Playground%20TV%20-%20Question`}
          className="neo-card block p-5 mb-8 bg-[#FFDE00] hover:-translate-y-0.5 transition-transform"
          data-testid="faq-contact-us"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg border-2 border-[#0A0A0A] bg-white flex items-center justify-center shrink-0 shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]">
              <Mail size={18} strokeWidth={2.5} className="text-[#0A0A0A]" />
            </div>
            <div className="min-w-0">
              <p className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A] leading-tight">Contact Us</p>
              <p className="text-sm text-[#0A0A0A]/80 font-['Nunito']">
                See a broken video, have a question, or a suggestion? Drop us a line at{' '}
                <span className="font-bold underline break-all">{CONTACT_EMAIL}</span>
              </p>
            </div>
          </div>
        </a>

        {/* Quick Links */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-10">
          {[
            { label: 'Set Parental PIN', to: '/settings', icon: Shield, color: '#4AC6FF' },
            { label: 'Kid Profiles', to: '/profiles', icon: Users, color: '#FF3860' },
            { label: 'Support Us', to: '/donate', icon: Crown, color: '#FFDE00' },
          ].map(link => (
            <Link key={link.label} to={link.to} className="neo-card p-3 flex items-center gap-2 bg-white text-sm" data-testid={`faq-link-${link.label.toLowerCase().replace(/ /g, '-')}`}>
              <link.icon size={16} strokeWidth={2.5} style={{ color: link.color }} />
              <span className="font-['Fredoka'] font-bold text-[#0A0A0A]">{link.label}</span>
            </Link>
          ))}
        </div>

        {/* FAQ Sections */}
        <div className="space-y-6">
          {faqs.map((section) => (
            <div key={section.category} className="neo-card p-5 sm:p-6 bg-white" data-testid={`faq-section-${section.category.toLowerCase().replace(/ /g, '-')}`}>
              <div className="flex items-center gap-2 mb-4 pb-3 border-b-2 border-[#0A0A0A]">
                <div className="w-8 h-8 rounded-lg border-2 border-[#0A0A0A] flex items-center justify-center shadow-[2px_2px_0px_0px_rgba(10,10,10,1)]" style={{ backgroundColor: section.color }}>
                  <section.icon size={16} strokeWidth={2.5} color={section.color === '#FFDE00' || section.color === '#39FF14' ? '#0A0A0A' : 'white'} />
                </div>
                <h2 className="font-['Fredoka'] text-lg font-bold text-[#0A0A0A]">{section.category}</h2>
              </div>
              <div>
                {section.items.map((item, i) => <FAQItem key={i} item={item} />)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
